import dayjs from 'dayjs'
import React, { createContext, useContext, useReducer } from 'react'
import { useSavingsToolkitApi } from '../api/savings-toolkit-api'
import {
  StkBucket,
  StkBucketActivity,
  StkBucketCategory,
  StkBucketRules,
  StkBucketUpdates,
  TransferDetails,
} from '../types'
import { API_STATUS } from '../utils/constants'
import { useStkBoosters } from './StkBoostersProvider'

interface NewBucket {
  bucketCategoryId: number
  bucketNickName: string
}

// Available to context consumers
export interface StkBucketsProps {
  buckets: StkBucket[] | null
  coreFirstBuckets: StkBucket[] | null
  categories: StkBucketCategory[] | null
  rules: StkBucketRules | null
  activity: Record<string, StkBucketActivity>
  hasBucketsError: boolean
  hasCategoriesError: boolean
  isBucketsLoading: boolean
  isCategoriesLoading: boolean
  hasRulesError: boolean
  isRulesLoading: boolean
  isActivityLoading: boolean
  hasActivityError: boolean
  addBuckets: (buckets: NewBucket[]) => Promise<void>
  getBuckets: () => Promise<void>
  getBucketCategories: () => Promise<void>
  getBucketRules: () => Promise<void>
  getBucketActivity: (bucketId: string) => Promise<void>
  deleteBucket: (
    bucketAccountId: string,
    needStateUpdate?: boolean,
  ) => Promise<StkBucket[]>
  updateBuckets: (
    bucketUpdates: StkBucketUpdates[],
    needStateUpdate?: boolean,
  ) => Promise<StkBucket[]>
  updateRules: (rulesUpdate: Partial<StkBucketRules>) => Promise<StkBucketRules>
  transferMoney: (transferDetails: TransferDetails) => Promise<void>
  redistributeMoney: (
    redistributeBuckets: Partial<StkBucket>[],
  ) => Promise<void>
}

interface IBucketsState {
  buckets: {
    status: API_STATUS
    data: StkBucket[] | null
  }
  categories: { status: API_STATUS; data: StkBucketCategory[] | null }
  rules: { status: API_STATUS; data: StkBucketRules | null }
  activity: {
    status: API_STATUS
    data: Record<string, StkBucketActivity>
  }
}

type IReducerAction =
  | {
      type: 'SET_BUCKETS'
      buckets: StkBucket[]
    }
  | {
      type: 'SET_CATEGORIES'
      categories: StkBucketCategory[]
    }
  | {
      type: 'SET_RULES'
      rules: StkBucketRules | null
    }
  | {
      type: 'SET_ACTIVITY'
      activity: StkBucketActivity
      bucketId: string
    }
  | { type: 'SET_BUCKET_STATUS'; status: API_STATUS }
  | { type: 'SET_CATEGORY_STATUS'; status: API_STATUS }
  | { type: 'SET_RULES_STATUS'; status: API_STATUS }
  | { type: 'SET_ACTIVITY_STATUS'; status: API_STATUS }

export const StkBucketsContext = createContext({} as StkBucketsProps)

export const bucketsReducer = (
  state: IBucketsState,
  action: IReducerAction,
): IBucketsState => {
  switch (action.type) {
    case 'SET_BUCKET_STATUS':
      return { ...state, buckets: { ...state.buckets, status: action.status } }
    case 'SET_CATEGORY_STATUS':
      return {
        ...state,
        categories: { ...state.categories, status: action.status },
      }
    case 'SET_RULES_STATUS':
      return {
        ...state,
        rules: { ...state.rules, status: action.status },
      }
    case 'SET_ACTIVITY_STATUS':
      return {
        ...state,
        activity: { ...state.activity, status: action.status },
      }
    case 'SET_BUCKETS': {
      const { buckets } = action
      const coreSavings = buckets.filter(bucket => bucket.isCoreSavings)
      const customBuckets = buckets
        .filter(bucket => !bucket.isCoreSavings)
        .sort((a, b) =>
          dayjs(a.bucketCreationDate).isBefore(dayjs(b.bucketCreationDate))
            ? 1
            : -1,
        )
      const orderedBuckets = [...customBuckets, ...coreSavings]
      return { ...state, buckets: { ...state.buckets, data: orderedBuckets } }
    }
    case 'SET_CATEGORIES':
      return {
        ...state,
        categories: { ...state.categories, data: action.categories },
      }
    case 'SET_RULES':
      return {
        ...state,
        rules: { ...state.rules, data: action.rules },
      }
    case 'SET_ACTIVITY':
      return {
        ...state,
        activity: {
          ...state.activity,
          data: {
            ...state.activity.data,
            [action.bucketId]: action.activity,
          },
        },
      }
    default: {
      return state
    }
  }
}

export const StkBucketsProvider: React.FC = ({ children }) => {
  const api = useSavingsToolkitApi()
  const { refreshBoostersForDeletedBucket } = useStkBoosters()

  const [state, dispatch] = useReducer(bucketsReducer, {
    buckets: { status: API_STATUS.IDLE, data: null },
    categories: { status: API_STATUS.IDLE, data: null },
    rules: { status: API_STATUS.IDLE, data: null },
    activity: { status: API_STATUS.IDLE, data: {} },
  })

  const getBuckets = async (): Promise<void> => {
    dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.PENDING })
    try {
      const buckets = await api.getStkBuckets()
      dispatch({ type: 'SET_BUCKETS', buckets })
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      console.error(e)
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.REJECTED })
    }
  }

  const getBucketCategories = async (): Promise<void> => {
    dispatch({ type: 'SET_CATEGORY_STATUS', status: API_STATUS.PENDING })
    try {
      const categories = await api.getStkBucketCategories()
      dispatch({ type: 'SET_CATEGORIES', categories })
      dispatch({ type: 'SET_CATEGORY_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      console.error(e)
      dispatch({ type: 'SET_CATEGORY_STATUS', status: API_STATUS.REJECTED })
    }
  }

  const getBucketRules = async (): Promise<void> => {
    dispatch({ type: 'SET_RULES_STATUS', status: API_STATUS.PENDING })
    try {
      const rules = await api.getStkBucketRules()
      dispatch({ type: 'SET_RULES', rules })
      dispatch({ type: 'SET_RULES_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      console.error(e)
      dispatch({ type: 'SET_RULES_STATUS', status: API_STATUS.REJECTED })
    }
  }

  const getBucketActivity = async (bucketId: string): Promise<void> => {
    dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.PENDING })
    try {
      const activity = await api.getStkBucketActivity(bucketId)
      dispatch({ type: 'SET_ACTIVITY', activity, bucketId })
      dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      /* istanbul ignore next */
      console.error(e)
      /* istanbul ignore next */
      dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.REJECTED })
    }
  }

  const deleteBucket = async (
    accountBucketId: string,
    needStateUpdate?: boolean,
  ): Promise<StkBucket[]> => {
    const buckets = await api.deleteStkBucket([{ accountBucketId }])
    dispatch({ type: 'SET_BUCKETS', buckets })
    await getBucketRules()
    refreshBoostersForDeletedBucket(accountBucketId)
    // TODO: This is temporary workaround and we need to follow up with metronome team to fix the modal close bug
    // https://jira.int.ally.com/browse/MWT-720
    // The modal is closing itself and screen is freezing when user delete last bucket
    // This state update is solving the screen freeze issue after delete last bucket
    // istanbul ignore next
    if (needStateUpdate) {
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.PENDING })
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.RESOLVED })
    }

    return buckets
  }

  const updateBuckets = async (
    bucketUpdates: StkBucketUpdates[],
    needStateUpdate?: boolean,
  ): Promise<StkBucket[]> => {
    const buckets = await api.updateStkBucketDetails(bucketUpdates)
    dispatch({ type: 'SET_BUCKETS', buckets })
    await getBucketRules()
    // TODO: This is temporary workaround and we need to follow up with metronome team to fix the modal close bug
    // https://jira.int.ally.com/browse/MWT-720
    // The modal is closing itself and screen is freezing when user delete bucket goal from ctas
    // This state update is solving the screen freeze issue after delete bucket goals
    // istanbul ignore next
    if (needStateUpdate) {
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.PENDING })
      dispatch({ type: 'SET_BUCKET_STATUS', status: API_STATUS.RESOLVED })
    }

    return buckets
  }

  const updateRules = async (
    rulesUpdate: Partial<StkBucketRules>,
  ): Promise<StkBucketRules> => {
    const rules = await api.updateStkBucketRules(rulesUpdate)
    dispatch({ type: 'SET_RULES', rules })
    return rules
  }

  const addBuckets = async (newBuckets: NewBucket[]): Promise<void> => {
    const buckets = await api.createStkBuckets(newBuckets)
    // istanbul ignore else - error handling is handled and tested inside the component
    if (buckets) {
      getBucketRules()
      dispatch({ type: 'SET_BUCKETS', buckets })
    }
  }

  const transferMoney = async (
    transferDetails: TransferDetails,
  ): Promise<void> => {
    const buckets = await api.transferStkBuckets(transferDetails)
    dispatch({ type: 'SET_BUCKETS', buckets })
  }

  const redistributeMoney = async (
    redistributedBuckets: Partial<StkBucket>[],
  ): Promise<void> => {
    const buckets = await api.redistributeStkBuckets(redistributedBuckets)
    dispatch({ type: 'SET_BUCKETS', buckets })
  }

  return (
    <StkBucketsContext.Provider
      value={{
        // General data
        buckets: state.buckets.data,
        coreFirstBuckets: state.buckets.data
          ? [
              ...state.buckets.data.filter(b => b.isCoreSavings),
              ...state.buckets.data.filter(b => !b.isCoreSavings),
            ]
          : null,
        categories: state.categories.data,
        rules: state.rules.data,
        activity: state.activity.data,

        // Error/loading states
        hasBucketsError: state.buckets.status === API_STATUS.REJECTED,
        isBucketsLoading: state.buckets.status === API_STATUS.PENDING,
        hasCategoriesError: state.categories.status === API_STATUS.REJECTED,
        isCategoriesLoading: state.categories.status === API_STATUS.PENDING,
        hasRulesError: state.rules.status === API_STATUS.REJECTED,
        isRulesLoading: state.rules.status === API_STATUS.PENDING,
        isActivityLoading: state.activity.status === API_STATUS.PENDING,
        hasActivityError: state.activity.status === API_STATUS.REJECTED,

        // Fetching functions
        addBuckets,
        getBuckets,
        getBucketCategories,
        getBucketRules,
        getBucketActivity,
        deleteBucket,
        updateBuckets,
        updateRules,
        transferMoney,
        redistributeMoney,
      }}
    >
      {children}
    </StkBucketsContext.Provider>
  )
}

export const useStkBuckets = (): StkBucketsProps =>
  useContext(StkBucketsContext)
